/*
 * What a search can claim about what it did not find (frontend performance plan, W1). Search reads
 * an index that can lag the list: history imported before it existed, a conversation whose indexing
 * failed. Only a complete index lets an empty result say nothing matches; otherwise the list says how
 * much search covers, so missing results never look like an answer.
 */
import { appCount } from "../../src/app-dates.ts";
import type { InboxSearchStatus } from "../../src/inbox-v2/contract.ts";

/** How much search covers here; null where no search is typed, or its status is still on its way. */
export type SearchCoverage = Pick<
  InboxSearchStatus,
  "state" | "eligible" | "searchable" | "failed"
>;

/** The coverage of a typed search: the server's status, `unknown` when that status could not be read. */
export function searchCoverage(
  searched: boolean,
  status: { data: InboxSearchStatus | undefined; isError: boolean },
): SearchCoverage | null {
  if (!searched) return null;
  if (status.data) return status.data;
  return status.isError
    ? { state: "unknown", eligible: null, searchable: null, failed: null }
    : null;
}

/** The sentence beside results that may be missing some; null when search covers everything. */
export function coverageNote(coverage: SearchCoverage | null): string | null {
  if (coverage === null || coverage.state === "complete") return null;
  if (coverage.state === "unknown" || coverage.eligible === null || coverage.searchable === null)
    return "Search may not cover every conversation here yet, so results may be missing.";
  const failed =
    coverage.failed && coverage.failed > 0
      ? ` ${appCount(coverage.failed)} could not be indexed.`
      : "";
  return `Search covers ${appCount(coverage.searchable)} of ${appCount(coverage.eligible)} conversations here, so results may be missing.${failed}`;
}
